import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    type ChartOptions,
    LinearScale,
    Tooltip,
} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {useTranslation} from 'react-i18next';
import {ScenarioDifficulty} from '@types';
import {Overlay} from '@goproblems/ui';
import ProgressPopover from '@components/ProgressPopover';
import {useBarChartTooltip} from '@components/hooks/useBarChartTooltip';
import generateInvasionBarColor from './generateInvasionBarColor';
import {invasionGoldBorderPlugin} from './invasionGoldBorderPlugin';

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip);
const difficulties = Object.values(ScenarioDifficulty);

const InvasionProgressChart = ({
    progress,
    showLabels = false,
}: {
    progress: Record<string, {solved: number; total: number}>;
    showLabels?: boolean;
}) => {
    const {t} = useTranslation();
    const {activeTooltip, externalTooltip, tooltipTarget} = useBarChartTooltip();
    const percentages = difficulties.map(difficulty => {
        const {solved, total} = progress[difficulty];
        return total > 0 ? Math.round((solved / total) * 100) : 0;
    });
    const data = {
        labels: difficulties,
        datasets: [
            {
                categoryPercentage: 0.8,
                barPercentage: 1,
                data: percentages,
                backgroundColor: percentages.map(generateInvasionBarColor),
                borderWidth: 0,
                borderRadius: 0,
                maxBarThickness: showLabels ? 24 : undefined,
            },
        ],
    };
    const options: ChartOptions<'bar'> = {
        maintainAspectRatio: false,
        interaction: {intersect: false, mode: 'index'},
        plugins: {
            legend: {display: false},
            tooltip: {enabled: false, external: externalTooltip},
        },
        scales: {
            y: {display: false, suggestedMax: 100, beginAtZero: true},
            x: {display: false},
        },
        layout: {padding: {top: 2}},
    };
    const difficulty = activeTooltip && difficulties[activeTooltip.dataIndex];
    const tooltipData = difficulty && progress[difficulty];

    return (
        <div className="flex h-full min-h-0 flex-col gap-1">
            <div className="relative min-h-0 flex-1">
                <Bar
                    data={data}
                    options={options}
                    plugins={[invasionGoldBorderPlugin]}
                />
            </div>
            {showLabels && (
                <div className="grid shrink-0 grid-cols-8 text-center text-[10px] leading-4 text-neutral-500 dark:text-neutral-400">
                    {difficulties.map(label => (
                        <span key={label}>{label}</span>
                    ))}
                </div>
            )}
            {activeTooltip && tooltipData && (
                <Overlay
                    show
                    target={tooltipTarget}
                    placement="top"
                    className="pointer-events-none">
                    <ProgressPopover
                        title={`${t('invasions.difficulty')}: ${difficulty}`}
                        metrics={[
                            {
                                label: t('levels.solved'),
                                value: `${tooltipData.solved} / ${tooltipData.total}`,
                            },
                            {
                                label: t('levels.progress'),
                                value: `${percentages[activeTooltip.dataIndex]}%`,
                            },
                        ]}
                    />
                </Overlay>
            )}
        </div>
    );
};

export default InvasionProgressChart;
